import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Leaf } from "lucide-react";
import PlantImage from "./PlantImage";
import { useCart } from "../../context/CartContext";
import { euro } from "../../utils/format";

export default function ProductCard({ plant, index = 0 }) {
  const { addItem } = useCart();
  const outOfStock = plant.stock <= 0;
  const onSale = plant.compareAt && plant.compareAt > plant.price;

  return (
    <motion.article
      initial={{ opacity: 0, y: 8 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.22, delay: Math.min(index * 0.02, 0.08) }}
      className="group surface overflow-hidden transition-shadow duration-300 hover:shadow-lift"
    >
      <Link to={`/shop/${plant.slug}`} className="block">
        <div className="relative">
          <PlantImage src={plant.images[0]} alt={`${plant.name} — ${plant.latinName}`} />
          {plant.traits.petSafe && (
            <span className="absolute left-3 top-3 chip bg-canvas-raised/95 backdrop-blur">
              <Leaf size={12} className="text-forest-500" /> Pet safe
            </span>
          )}
          {onSale && !outOfStock && (
            <span className="absolute right-3 top-3 rounded-full bg-terracotta px-2.5 py-1 text-[11px] font-semibold text-canvas-raised">
              Sale
            </span>
          )}
          {outOfStock && (
            <div className="absolute inset-0 flex items-end bg-gradient-to-t from-green-900/55 to-transparent p-4">
              <span className="rounded-full bg-canvas-raised/95 px-3 py-1 text-xs font-semibold text-ink">
                Back in spring
              </span>
            </div>
          )}
        </div>
      </Link>

      <div className="p-4">
        <p className="text-[11px] uppercase tracking-wide text-ink-soft">{plant.category}</p>
        <Link to={`/shop/${plant.slug}`}>
          <h3 className="font-display text-xl leading-tight text-ink group-hover:text-forest-700">
            {plant.name}
          </h3>
        </Link>
        <p className="mb-3 mt-0.5 text-sm italic text-ink-soft">{plant.latinName}</p>

        <div className="flex items-center justify-between">
          <div className="flex items-baseline gap-2">
            <span className="t-price">{euro(plant.price)}</span>
            {onSale && (
              <span className="text-sm text-ink-soft line-through">{euro(plant.compareAt)}</span>
            )}
          </div>
          {!outOfStock ? (
            <button
              onClick={() => addItem(plant, 1)}
              aria-label={`Add ${plant.name} to cart`}
              className="flex h-10 w-10 items-center justify-center rounded-full bg-forest-700 text-canvas-raised transition-all hover:bg-forest-900 hover:scale-105"
            >
              <Plus size={18} />
            </button>
          ) : (
            <span className="text-xs font-medium text-ink-soft">Sold out</span>
          )}
        </div>
      </div>
    </motion.article>
  );
}
